import React, { Component } from 'react';
import HowToLayout from './HowToLayout';

class HowToPowerLevel extends Component {
    render() {
        return (
            <HowToLayout title="Power Level">
                <p className="howto-intro">
                    Your <strong className="howto-strong">Power Level</strong> is a score that reflects how active you are in the AniMates community. It shows up as a stat on your profile, and the main way to grow it is by actually showing up — checking in at events.
                </p>

                <section className="howto-section">
                    <h3 className="howto-section-title" style={{fontFamily: "'Chelsea Market', cursive"}}>How You Earn It</h3>
                    <p className="howto-paragraph">
                        Power Level is earned by <strong className="howto-strong">checking in at events</strong>, and there are two ways to do it depending on the kind of event.
                    </p>
                    <p className="howto-paragraph">
                        At a <strong className="howto-strong">member meetup</strong>, the host displays a check-in QR code and you scan it. At a <strong className="howto-strong">Featured event</strong> — the official events AniMates curates, like conventions — there's no host holding a code, so you check in with your location instead: tap the <strong className="howto-strong">Power Up</strong> bolt while you're actually there.
                    </p>
                    <p className="howto-paragraph">
                        Either way, the rule is the same — you have to genuinely show up. The more events you attend, the higher it climbs.
                    </p>
                </section>

                <section className="howto-section">
                    <h3 className="howto-section-title" style={{fontFamily: "'Chelsea Market', cursive"}}>The Power Level System</h3>
                    <p className="howto-paragraph">
                        Here's what each scenario is worth:
                    </p>
                    <div className="howto-points">
                        <div className="howto-points-row">
                            <span className="howto-points-label"><strong className="howto-strong">Attending</strong> — check in at an event you joined</span>
                            <span className="howto-points-value">+20</span>
                        </div>
                        <div className="howto-points-row">
                            <span className="howto-points-label"><strong className="howto-strong">Hosting</strong> — for every attendee who checks in to your event</span>
                            <span className="howto-points-value">+20</span>
                        </div>
                        <div className="howto-points-row">
                            <span className="howto-points-label"><strong className="howto-strong">Featured event</strong> — Power Up check-in at an official AniMates event</span>
                            <span className="howto-points-value">+5</span>
                        </div>
                        <div className="howto-points-row">
                            <span className="howto-points-label"><strong className="howto-strong">Premium bonus</strong> — added on top of every meetup check-in, attending or hosting</span>
                            <span className="howto-points-value">+10</span>
                        </div>
                        <div className="howto-points-row">
                            <span className="howto-points-label"><strong className="howto-strong">Premium welcome bonus</strong> — one time, when you go premium</span>
                            <span className="howto-points-value">+1,500</span>
                        </div>
                    </div>
                    <p className="howto-paragraph">
                        At member meetups the premium bonus stacks with the base, so a premium member earns <strong className="howto-strong">+30</strong> per check-in. Hosting rewards a full turnout — a packed event you host adds up fast. There are no penalties; Power Level only goes up.
                    </p>
                    <p className="howto-paragraph">
                        <strong className="howto-strong">Featured events are a flat +5 for everyone</strong> — the premium bonus doesn't apply there, and they're worth less than a member meetup on purpose. Meetups are the heart of AniMates, and showing up to one that another member organised counts for more.
                    </p>
                    <p className="howto-points-note">
                        Point values may be adjusted over time as we tune the system.
                    </p>
                </section>

                <section className="howto-section">
                    <h3 className="howto-section-title" style={{fontFamily: "'Chelsea Market', cursive"}}>Checking In as an Attendee</h3>
                    <p className="howto-paragraph">
                        At the event, open it from your itinerary and tap the <strong className="howto-strong">QR scanner</strong> icon, then point your camera at the host's QR code. You'll need to allow camera access the first time. When it works, you'll see <strong className="howto-strong">"Checked In!"</strong> and your Power Level goes up. Check-in is only open during the event and for a short window after it ends, so scan while you're there.
                    </p>
                </section>

                <section className="howto-section">
                    <h3 className="howto-section-title" style={{fontFamily: "'Chelsea Market', cursive"}}>Hosting: Show Your QR Code</h3>
                    <p className="howto-paragraph">
                        If you're the host, open your event and tap the <strong className="howto-strong">QR code</strong> icon to display your code with <strong className="howto-strong">"Scan to check in."</strong> Hold it up so attendees can scan it as they arrive. This is how everyone at your event gets credited.
                    </p>
                </section>

                <section className="howto-section">
                    <h3 className="howto-section-title" style={{fontFamily: "'Chelsea Market', cursive"}}>Checking In at a Featured Event</h3>
                    <p className="howto-paragraph">
                        Featured events are the official ones AniMates curates — conventions, screenings, pop-ups. Nobody is standing there with a QR code, so check-in works from your location instead.
                    </p>
                    <p className="howto-paragraph">
                        Join the event, then on the day open it from <strong className="howto-strong">Itinerary → Attending</strong> and tap the <strong className="howto-strong">Power Up</strong> bolt. You'll need to allow location access the first time. If you're there, you'll be checked in and your Power Level goes up.
                    </p>
                    <p className="howto-paragraph">
                        Three things to know: you have to be <strong className="howto-strong">within 3 miles</strong> of the venue, the bolt only works <strong className="howto-strong">once the event has started</strong> (before then it'll tell you to come back), and you can only power up <strong className="howto-strong">once per event</strong>.
                    </p>
                </section>

                <section className="howto-section">
                    <h3 className="howto-section-title" style={{fontFamily: "'Chelsea Market', cursive"}}>Premium Bonus</h3>
                    <p className="howto-paragraph">
                        Premium members earn a bonus to their Power Level on every <strong className="howto-strong">member meetup</strong> check-in, attending or hosting, so the same events count for a little more. Featured events are a flat amount for everyone and don't carry the bonus.
                    </p>
                </section>

                <section className="howto-section">
                    <h3 className="howto-section-title" style={{fontFamily: "'Chelsea Market', cursive"}}>Where to See It</h3>
                    <p className="howto-paragraph">
                        Your Power Level appears alongside your other stats on your community profile, and you'll see it on other members' profiles too — a quick signal of who's active and showing up in the community.
                    </p>
                </section>

                <p className="howto-footer-note">
                    Still need a hand? Email <a href="mailto:hello@animates.app" className="howto-link">hello@animates.app</a>.
                </p>
            </HowToLayout>
        );
    }
}

export default HowToPowerLevel;
